import { logger } from '@beelday/common'
import { useSelector } from 'app-redux'
import { WINDOW_VIEW } from 'presenter-mode/window-role'
import { useEffect } from 'react'
import useIsTrainer from 'room/common/use-is-trainer'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useSessionMode } from 'session-mode/use-session-mode'
import { autoStartKey, shouldAutoStartScript } from './auto-start'
import { browserLocks, claimAutoStart } from './auto-start-claim'
import {
	selectScriptHasItems,
	selectScriptLoaded,
	selectScriptStarted,
} from './script-redux'
import useScript from './use-script'

type Attempts = Pick<Set<string>, 'has' | 'add'>

/**
 * The schemes this page has tried to start. It lives outside the component on
 * purpose: the room unmounts when the trainer walks to the break room or the
 * lobby, and coming back is no new entry. A reload is - the page is new.
 */
const attemptedSchemes = new Set<string>()

/**
 * Starts the in-person trainer's agenda as they walk into the training room,
 * so the first item is current without a click on Start script. Mounted by
 * the in-person trainer's room; `shouldAutoStartScript` is the whole rule.
 *
 * One attempt per scheme and page, made before the request is sent: a
 * re-render, a second effect run or an answer still on its way cannot send
 * another, and a failed start is logged and left to the Start script bar,
 * which stays on screen because the script did not start. Nothing retries.
 */
export const useAutoStartScript = (
	attempts: Attempts = attemptedSchemes
): void => {
	const script = useScript()
	const address = useAssertedJoinedRoomAddress()
	const inPerson = useSessionMode() === 'IN_PERSON'
	const isTrainer = useIsTrainer()
	const loaded = useSelector(selectScriptLoaded)
	const started = useSelector(selectScriptStarted)
	const hasItems = useSelector(selectScriptHasItems)
	const key = autoStartKey(address)

	useEffect(() => {
		if (
			!shouldAutoStartScript({
				inPerson,
				isTrainer,
				projector: WINDOW_VIEW.role === 'projector',
				loaded,
				started,
				hasItems,
				alreadyAttempted: attempts.has(key),
			})
		) {
			return
		}
		attempts.add(key)
		claimAutoStart(browserLocks(), key, () => script.start(address)).catch(
			error => logger.error('Starting the script automatically failed', error)
		)
	}, [
		attempts,
		script,
		address,
		key,
		inPerson,
		isTrainer,
		loaded,
		started,
		hasItems,
	])
}

export default useAutoStartScript
